//
//  Login.tsx
//  TrajeXY Signet Server
//
//  Copyright © 2026 TrajeXY. All rights reserved.
//
//  Created: September 21, 2026
//  Author: Bob Keathley
//
//  Tenant sign-in form with a Forgot password link. The forgot endpoint always
//  answers 202 (SIG-66), so no account is revealed.
//
import { useState, type FormEvent } from 'react'
import { api } from '@shared/api/client'
import { auth } from '@shared/auth'
import BrandMark from '@shared/components/BrandMark'
import PasswordInput from '@shared/components/PasswordInput'

export default function Login() {
  const [forgot, setForgot] = useState(false)
  const [email, setEmail] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    // Read both fields from the form so browser autofill counts even before React sees a change.
    const f = new FormData(e.currentTarget)
    const emailValue = String(f.get('email') ?? '').trim()
    const password = String(f.get('password') ?? '')
    if (!emailValue || !password) { setError('Enter your email and password.'); return }
    setBusy(true)
    setError(null)
    const r = await auth.login(emailValue, password)
    setBusy(false)
    if (!r.ok) setError(r.error)
  }

  if (forgot) return <ForgotPassword initialEmail={email} onBack={() => setForgot(false)} />
  return (
    <div className="login">
      <form className="card" onSubmit={submit} noValidate aria-label="Sign in">
        <BrandMark variant="mark" size={88} />
        <p className="brand">
          <b>Signet</b> Console
        </p>
        <p className="hint">Sign in with your tenant account.</p>
        <label htmlFor="email">Email</label>
        <input id="email" name="email" type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} autoFocus />
        <label htmlFor="password">Password</label>
        <PasswordInput id="password" name="password" autoComplete="current-password" required />
        {error && (
          <p className="err" role="alert">
            {error}
          </p>
        )}
        <div style={{ marginTop: 20 }}>
          <button className="btn" type="submit" disabled={busy} style={{ width: '100%' }}>
            {busy ? 'Signing in…' : 'Sign in'}
          </button>
        </div>
        <p style={{ marginTop: 12, textAlign: 'center' }}>
          <button type="button" className="btn ghost sm" onClick={() => setForgot(true)}>Forgot password?</button>
        </p>
        <p className="hint" style={{ marginTop: 16 }}>
          No self-service sign-up. Your tenant's owner or security-admin creates accounts. Platform
          staff sign in at Signet Command.
        </p>
      </form>
    </div>
  )
}

// SIG-66: `POST /v1/auth/forgot` always answers 202 whatever the email, so the confirmation never varies.
function ForgotPassword({ initialEmail, onBack }: { initialEmail: string; onBack: () => void }) {
  const [email, setEmail] = useState(initialEmail)
  const [busy, setBusy] = useState(false)
  const [sent, setSent] = useState(false)

  async function submit(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    await api('/auth/forgot', { method: 'POST', body: JSON.stringify({ email: email.trim(), console: 'tenant' }) })
    setBusy(false)
    setSent(true)   // identical confirmation on success or failure: never reveal whether the account exists
  }

  return (
    <div className="login">
      <form className="card" onSubmit={submit} aria-label="Forgot password">
        <BrandMark variant="mark" size={88} />
        <p className="brand">
          <b>Signet</b> Console
        </p>
        {sent ? (
          <p className="hint" role="status">If an account exists for that email, we sent a reset link.</p>
        ) : (
          <>
            <p className="hint">Enter your email and we'll send a link to set a new password.</p>
            <label htmlFor="fp-email">Email</label>
            <input id="fp-email" type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} autoFocus />
            <div style={{ marginTop: 20 }}>
              <button className="btn" type="submit" disabled={busy || !email} style={{ width: '100%' }}>{busy ? 'Sending…' : 'Send reset link'}</button>
            </div>
          </>
        )}
        <p style={{ marginTop: 12, textAlign: 'center' }}>
          <button type="button" className="btn ghost sm" onClick={onBack}>Back to sign in</button>
        </p>
      </form>
    </div>
  )
}
